import { Injectable, computed, signal } from '@angular/core';
import { environment } from '../../../environments/environment';
import { Me, MeService } from './me.service';
import { Ability } from '../models/ability-catalog.model';

// Hub's ability id is the access-control grant-token grammar minted by
// wg-agents-api's ability_catalog.py (_gateway_ability_id) —
// "GATEWAY_<TARGET_UPPER>::<op_slug>", e.g. "GATEWAY_SALESFORCE_READ::query_accounts".
// Studio's own catalog (GET /tools) identifies the SAME gateway tool by its raw
// wire name instead — "<target-lower>___<op>", e.g.
// "salesforce-read___query_accounts" (see wg-agents-studio-ui ability-catalog.ts
// abilityForGatewayTool). The two apps never agreed on one id space, so a
// gateway-derived id handed to Studio as-is matches nothing in its catalog and
// silently equips zero abilities. `Ability.gatewayKey` (GET /agenthome/abilities)
// is the hub API publishing that exact Studio wire name alongside the
// grant-token id — that's the one to hand off; see toStudioAbilityId.
//
// FALLBACK: if a row somehow has no gatewayKey (e.g. an older cached hub-api
// response, or a genuinely non-gateway ability Studio never had anyway), this
// reconstructs a best-guess wire name by reversing the grant-token
// normalisation: lower-case the target and turn its underscores back into
// hyphens. That reversal is a heuristic, not a guarantee — it only holds
// because every gateway target registered today (salesforce-read,
// atlassian-read, catsy-read, ...) uses hyphens and never underscores in its
// name. Ids that aren't gateway-shaped pass through unchanged — Studio simply
// won't have a matching ability to pre-equip for those.
function toStudioAbilityId(ability: Pick<Ability, 'id' | 'gatewayKey'>): string {
  if (ability.gatewayKey) return ability.gatewayKey;
  const match = /^GATEWAY_([A-Z0-9_]+)::(.+)$/.exec(ability.id);
  if (!match) return ability.id;
  const [, target, op] = match;
  return `${target.toLowerCase().replace(/_/g, '-')}___${op}`;
}

/**
 * One source of truth for which top-level hub surfaces the caller can reach —
 * Workspace, Apps, Dashboards. The chat shell gates its own nav inline; the
 * Workspace/Apps pages carry the SAME primary rail (so collapsing the section
 * nav never strands the user), and they gate its links through this service so
 * a user never sees a rail icon that dead-ends at the route guard.
 *
 * The visibility rules mirror AgentHomeChatComponent: a runtime surface
 * kill-switch (GET /agenthome/me → config-repo flags.json) with the build-time
 * environment flag as the fallback, the same access gates (a confirmed non-zero
 * apps_available for Apps, any data_access grant for Workspace), AND the same
 * deactivated-account defence (a still-open session for a deactivated user sees
 * no surfaces).
 *
 * /me is fetched once and shared: ensureLoaded() is idempotent, and the loaded
 * profile is exposed via `me()` so consumers read grant data from here instead
 * of issuing their own getMe().
 */
@Injectable({ providedIn: 'root' })
export class HubSurfacesService {
  private readonly _me = signal<Me | null>(null);
  /** The shared /me profile once loaded (null until then). */
  readonly me = this._me.asReadonly();
  /** True once a /me fetch has failed — lets the Studio/Admin gates fall back to
   *  the login claim during an outage, exactly like the chat shell. */
  private readonly _loadFailed = signal(false);
  private loading = false;

  constructor(private readonly meSvc: MeService) {}

  /** Fetch /me once. Idempotent; on error we clear the guard so a later page
   *  visit retries (don't cache a failed load and hide every rail link). */
  ensureLoaded(): void {
    if (this.loading || this._me()) return;
    this.loading = true;
    this.meSvc.getMe().subscribe({
      next: m => {
        this._me.set(m);
        this._loadFailed.set(false);
        // Keep sessionStorage 'current-role' fresh so the Studio/Admin portal
        // hand-off forwards the LIVE role (mirrors the chat shell); a deactivated
        // account has no live role, so drop the stale claim entirely.
        if (m.role) sessionStorage.setItem('current-role', m.role);
        else if (m.user_status !== 'active') sessionStorage.removeItem('current-role');
      },
      error: () => { this.loading = false; this._loadFailed.set(true); },
    });
  }

  /** A deactivated-but-still-open session must see no privileged surfaces —
   *  the chat zeroes these client-side because the backend does not reliably
   *  blank org/config-level flags per user. Unknown (pre-load) status defaults
   *  to active so nothing flickers hidden before /me answers. */
  private readonly active = computed(() => (this._me()?.user_status ?? 'active') === 'active');

  readonly showDashboards = computed(() =>
    this.active() && (this._me()?.surfaces?.dashboards ?? environment.showDashboards));

  /** Stats ("time saved") widget. Runtime gate is /me → surfaces.stats
   *  (config-repo hub_show_stats), build-time environment.showStats as fallback. */
  readonly showStats = computed(() =>
    this.active() && (this._me()?.surfaces?.stats ?? environment.showStats));

  /** Read-only Abilities catalog nav entry. Browse-only + access-safe, gated by
   *  the runtime surface flag (config-repo → /me, env flag as fallback) AND
   *  restricted to developer/admin/super_admin — not shown to plain end users. */
  readonly showAbilities = computed(() =>
    this.active() && (this._me()?.surfaces?.abilities ?? environment.showAbilities) &&
    (this.isAdmin() || this.isDeveloper()));

  readonly showApps = computed(() => {
    if (!this.active()) return false;
    const m = this._me();
    const enabled = m?.surfaces?.apps ?? environment.showApps;
    if (!enabled) return false;
    // Fails CLOSED on an unknown count (null = /me not loaded yet, older
    // backend, or a lookup blip): no confirmed app access, no launcher. Kept in
    // lockstep with AgentHomeChatComponent.showApps — if these two disagree the
    // rail offers a link the route guard then refuses.
    return (m?.apps_available ?? 0) > 0;
  });

  readonly showWorkspace = computed(() => {
    if (!this.active()) return false;
    const m = this._me();
    const enabled = m?.surfaces?.workspace ?? environment.showDataHub;
    return enabled && (!!m?.data_all || Object.keys(m?.data_access ?? {}).length > 0);
  });

  // ── Switch-app portals (Studio / Admin) ── The chat shell surfaces these in
  // its rail; the Workspace/Apps rail carries the same icons, gated identically:
  //  • loaded profile  → live is_admin / role (deactivated ⇒ nothing);
  //  • before /me       → the login claim (developer/approver only — don't flash
  //                       an Admin link that /me might revoke);
  //  • after a /me error → the full login claim, incl. admin/super_admin, so an
  //                       admin keeps the links during an outage (matches chat).
  private readonly claimRole = computed(() => (sessionStorage.getItem('current-role') ?? '').toLowerCase());

  readonly isAdmin = computed(() => {
    const m = this._me();
    if (m) return this.active() && !!m.is_admin;
    const r = this.claimRole();
    return this._loadFailed() && (r === 'admin' || r === 'super_admin');
  });

  readonly isStudio = computed(() => {
    const m = this._me();
    if (m) return this.active() && (!!m.is_admin || m.role === 'developer' || m.role === 'approver');
    const r = this.claimRole();
    if (this._loadFailed()) return r === 'admin' || r === 'super_admin' || r === 'developer' || r === 'approver';
    return r === 'developer' || r === 'approver';
  });

  /** Developer role only (no approver) — used to restrict the Abilities catalog
   *  to developer/admin/super_admin. */
  readonly isDeveloper = computed(() => {
    const m = this._me();
    if (m) return this.active() && m.role === 'developer';
    return this.claimRole() === 'developer';
  });

  /** Dev Tools portal is super_admin-only: the platform/config surfaces it
   *  hosts (flags, deployments, config registry) are never appropriate for a
   *  plain admin. Same load-order posture as `isAdmin`: prefer /me.role once
   *  loaded, fall back to the login claim only if /me failed. */
  readonly isDevTools = computed(() => {
    const m = this._me();
    if (m) return this.active() && m.role === 'super_admin';
    return this._loadFailed() && this.claimRole() === 'super_admin';
  });

  /** Open Studio, optionally deep-linking a set of abilities the Abilities
   *  catalog handed off (the builder pre-selects them once it consumes the
   *  param; harmless if it doesn't yet). Takes full Ability objects (not bare
   *  ids) because the id Studio needs to match (gatewayKey) differs from the
   *  id Hub uses internally — see toStudioAbilityId. */
  openStudioPortal(abilities?: Pick<Ability, 'id' | 'gatewayKey'>[]): void {
    const query = abilities?.length
      ? `?abilities=${encodeURIComponent(abilities.map(toStudioAbilityId).join(','))}`
      : '';
    this.openPortal(environment.STUDIO_PORTAL_URL, query);
  }
  openAdminPortal(): void { this.openPortal(environment.ADMIN_PORTAL_URL); }
  openDevToolsPortal(): void { this.openPortal(environment.DEVTOOLS_PORTAL_URL); }
  openAppsPortal(): void { this.openPortal(environment.APPS_PORTAL_URL); }

  /** Open a sibling WatchGuard portal in a new tab, forwarding the signed-in
   *  session (same encoding the chat shell uses). An optional query string is
   *  inserted before the #session fragment. */
  private openPortal(baseUrl: string, query = ''): void {
    const payload = {
      'id-token': sessionStorage.getItem('id-token') ?? '',
      'current-role': sessionStorage.getItem('current-role') ?? '',
      'current-username': sessionStorage.getItem('current-username') ?? '',
      'current-email': sessionStorage.getItem('current-email') ?? '',
      'STATIC': sessionStorage.getItem('STATIC') ?? '',
    };
    const bytes = new TextEncoder().encode(JSON.stringify(payload));
    const encoded = btoa(Array.from(bytes, b => String.fromCodePoint(b)).join(''));
    window.open(`${baseUrl}/${query}#session=${encoded}`, '_blank');
  }
}
